import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { Control, useFieldArray } from 'react-hook-form';
import StyledSelect from '../styled/StyledSelect';
import {
  COUNTRIES,
  COUNTRIES_DATA,
  CURRENCIES,
} from '../../../../shared/CountriesData';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface CircuitCountriesFormProps {
  control: Control<CircuitFormDataWithTranslations>;
  isViewMode?: boolean;
}

const CircuitCountriesForm = ({
  control,
  isViewMode = false,
}: CircuitCountriesFormProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'countries',
  });

  return (
    <div className="flex flex-col">
      {fields.map((field, indexField) => (
        <SingleCountryForm
          key={field.id}
          control={control}
          index={indexField}
          displayDeleteButton={fields.length > 1}
          onDelete={() => remove(indexField)}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          variant="link"
          className="w-fit p-0 text-brand-700"
          title={t('circuits.countries.addCountry')}
          onClick={() =>
            append({
              country: undefined,
              currency: undefined,
            } as unknown as CircuitFormData['countries'][number])
          }
          icon={<PlusIcon className="size-5 fill-brand-700" />}
        />
      )}
    </div>
  );
};

export default CircuitCountriesForm;

const SingleCountryForm = ({
  control,
  index,
  displayDeleteButton = false,
  onDelete,
  isViewMode = false,
}: {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  displayDeleteButton?: boolean;
  onDelete?: () => void;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();
  const updateCountryAndCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: `countries.${index}.country`,
    secondaryName: `countries.${index}.currency`,
    retrieveSecondary: (primaryValue) => {
      const countryData = COUNTRIES_DATA.find(
        (data) => data.Pays === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.label === countryData?.Devise,
      );
      return currency;
    },
  });

  return (
    <div className="flex flex-col">
      <div className="flex flex-col md:flex-row gap-4">
        <StyledSelect
          control={control}
          options={COUNTRIES}
          name={`countries.${index}.country`}
          title={t('circuits.countries.country')}
          placeholder={t('circuits.countries.countryPlaceholder')}
          onChange={updateCountryAndCurrency}
          disabled={isViewMode}
        />
        <StyledSelect
          control={control}
          options={CURRENCIES}
          name={`countries.${index}.currency`}
          title={t('circuits.countries.currency')}
          placeholder={t('circuits.countries.currencyPlaceholder')}
          disabled={isViewMode}
        />
      </div>
      {displayDeleteButton && !isViewMode && (
        <StyledButton
          title={t('circuits.countries.delete')}
          variant="link"
          className="w-fit p-0"
          onClick={onDelete}
        />
      )}
    </div>
  );
};
